import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Btn, Eyebrow } from "../components/Primitives";
import Logo from "../components/Logo";

const SLIDES = [
    {
        eyebrow: "Ever After",
        headline: "Discover your love archetype.",
        sub: "Built on the science of how people actually connect.",
    },
    {
        eyebrow: "Beyond the swipe",
        headline: "Find someone who actually gets you.",
        sub: "Not who you'd swipe right on at 1am.",
    },
    {
        eyebrow: "Beta access — by invitation",
        headline: "A new way of meeting.",
        sub: "Tap to begin.",
        features: [
            "Compatibility based on the latest science model",
            "Curated matches",
            "AI coach to increase healthy behaviors and romance in dating",
            "Curated dates powered by AI to increase the joy of dating",
        ],
    },
];

// Module-level constants — referenced by reference, never re-allocated per render.
const SLIDE_ANIM = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -16 },
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
};

function useDeferredVideo() {
    const [ready, setReady] = useState(false);
    useEffect(() => {
        const conn = navigator.connection;
        if (conn?.saveData || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        const t = setTimeout(() => setReady(true), 600);
        return () => clearTimeout(t);
    }, []);
    return ready;
}

export default function Welcome({ onBegin }) {
    const [idx, setIdx] = useState(0);
    const [touchStart, setTouchStart] = useState(null);
    const videoReady = useDeferredVideo();

    const next = () => setIdx((i) => Math.min(i + 1, SLIDES.length - 1));
    const prev = () => setIdx((i) => Math.max(i - 1, 0));

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "ArrowRight") next();
            if (e.key === "ArrowLeft") prev();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    });

    const onTouchStart = (e) => setTouchStart(e.touches[0].clientX);
    const onTouchEnd = (e) => {
        if (touchStart == null) return;
        const dx = e.changedTouches[0].clientX - touchStart;
        if (dx < -40) next();
        else if (dx > 40) prev();
        setTouchStart(null);
    };

    const slide = SLIDES[idx];
    const isLast = idx === SLIDES.length - 1;

    return (
        <div
            className="flex-1 flex flex-col relative bg-cream overflow-hidden max-h-screen"
            data-testid="welcome-screen"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
        >
            {/* Romantic video background */}
            <video
                data-testid="welcome-bg-video"
                className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                autoPlay
                muted
                loop
                playsInline
                preload="none"
                poster="/media/welcome-bg.jpg"
                src={videoReady ? "/media/welcome-bg.mp4" : undefined}
            />
            {/* Subtle white wash so the burgundy text stays crisp against any color motion */}
            <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-white/55 to-white pointer-events-none" />
            <div className="absolute inset-0 bg-burgundy/[0.03] pointer-events-none" />

            {/* Top brand */}
            <div className="relative z-10 px-6 pt-safe pb-2 flex items-center justify-between">
                <Logo size={56} />
                <div className="text-[10px] tracking-[0.32em] uppercase text-ink-muted">
                    {String(idx + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}
                </div>
            </div>

            {/* Slide content */}
            <div className={`relative z-10 flex-1 min-h-0 overflow-y-auto no-scrollbar ${idx === 0 ? "px-4 justify-start pt-2" : "px-6 sm:px-7 justify-center py-4"} flex flex-col`}>
                <AnimatePresence mode="wait">
                    <motion.div
                        key={idx}
                        initial={SLIDE_ANIM.initial}
                        animate={SLIDE_ANIM.animate}
                        exit={SLIDE_ANIM.exit}
                        transition={SLIDE_ANIM.transition}
                        className={idx === 0 ? "" : "space-y-4 sm:space-y-6"}
                    >
                        {idx !== 0 && <Eyebrow>{slide.eyebrow}</Eyebrow>}
                        <h1
                            className={
                                idx === 0
                                    ? "font-display text-[clamp(2.4rem,14vw,3.75rem)] leading-[0.88] tracking-[-0.05em] text-burgundy uppercase"
                                    : "font-display text-4xl sm:text-5xl leading-[1.02] tracking-tight text-burgundy"
                            }
                        >
                            {slide.headline}
                        </h1>
                        {idx !== 0 && (
                            <p className="text-base text-ink-muted leading-relaxed font-light max-w-sm">
                                {slide.sub}
                            </p>
                        )}

                        {slide.features && (
                            <ul
                                data-testid="welcome-features"
                                className="pt-5 space-y-0 border-t border-burgundy/15"
                            >
                                {slide.features.map((feat, i) => (
                                    <li
                                        key={feat}
                                        data-testid={`welcome-feature-${i}`}
                                        className="flex items-start gap-4 py-2.5 border-b border-burgundy/10 last:border-b-0"
                                    >
                                        <span className="font-display italic text-gold-deep text-sm leading-none pt-1.5 tracking-wider">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <span className="font-display text-base sm:text-lg leading-snug text-ink flex-1">
                                            {feat}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {/* Decorative ornament (hidden on first slide + features slide) */}
                        {idx !== 0 && !slide.features && (
                            <div className="pt-6 flex items-center gap-3 text-gold">
                                <span className="h-px flex-1 bg-burgundy/15" />
                                <span className="font-display italic text-sm tracking-wide">·</span>
                                <span className="h-px flex-1 bg-burgundy/15" />
                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Bottom nav */}
            <div className="relative z-10 px-6 sm:px-7 pt-3 pb-safe space-y-4 bg-gradient-to-t from-white via-white/90 to-transparent">
                <div className="flex justify-center gap-1.5">
                    {SLIDES.map((s, i) => (
                        <div
                            key={s.eyebrow}
                            className={`h-[2px] transition-all duration-500 ${
                                i === idx ? "w-8 bg-burgundy" : "w-3 bg-burgundy/20"
                            }`}
                        />
                    ))}
                </div>

                {isLast ? (
                    <Btn data-testid="welcome-begin-btn" onClick={onBegin}>
                        Begin assessment
                    </Btn>
                ) : (
                    <Btn data-testid="welcome-next-btn" onClick={next} variant="secondary">
                        Continue
                    </Btn>
                )}
                <p className="text-center text-[10px] tracking-[0.28em] uppercase text-ink-muted">
                    <a href="/login" data-testid="welcome-signin-link" className="tap-link underline underline-offset-4 hover:text-burgundy">
                        Already a member? Sign in
                    </a>
                </p>
                <p className="flex items-center justify-center gap-4 text-[9px] tracking-[0.28em] uppercase text-ink-light">
                    <a href="/tiers" data-testid="welcome-tiers-link" className="tap-link hover:text-burgundy underline-offset-4 hover:underline">
                        See membership tiers
                    </a>
                    <span aria-hidden="true">·</span>
                    <a href="/privacy" data-testid="welcome-privacy-link" className="tap-link hover:text-burgundy underline-offset-4 hover:underline">
                        Privacy policy
                    </a>
                </p>
            </div>
        </div>
    );
}
